/** Wervingsmodule — pure hulpfuncties (getest in __tests__/wervingLogica.test.js). */

export const INKT = '#1f2937';
export const STIL = '#5f6b7a';
export const LIJN = '#eceef1';
export const ACCENT = '#526075';
export const ACCENT_ZACHT = '#eef2f8';
export const KOLOM_BG = '#fafbfc';
export const PROBLEEM = '#b91c1c';

export const EIND_FASEN = ['aangenomen', 'afgewezen'];
export const OORDEEL_LABEL = { goedkeuren: 'Goedgekeurd', afkeuren: 'Afgekeurd' };
export const STATUS_LABEL = { concept: 'Concept', open: 'Open', gesloten: 'Gesloten' };

/** UTC-tijd van de server (zonder Z) → Date. */
export function alsDatum(iso) {
  if (!iso) return null;
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function dagenGeleden(iso, nu = new Date()) {
  const d = alsDatum(iso);
  if (!d) return '';
  const begin = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const n = Math.round((begin(nu) - begin(d)) / 86400000);
  if (n <= 0) return 'vandaag';
  if (n === 1) return 'gisteren';
  if (n < 14) return `${n} dagen geleden`;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

export function datumTijd(iso) {
  const d = alsDatum(iso);
  return d ? d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

export function groepeerPerFase(sollicitaties, fasen) {
  const uit = Object.fromEntries((fasen || []).map((f) => [f.sleutel, []]));
  for (const s of sollicitaties || []) (uit[s.fase] || (uit[s.fase] = [])).push(s);
  return uit;
}

export function zoek(sollicitaties, q) {
  const t = String(q || '').trim().toLowerCase();
  if (!t) return sollicitaties || [];
  return (sollicitaties || []).filter((s) => String(s.naam || '').toLowerCase().includes(t));
}

/** Positie in de stapel + vorige/volgende id. */
export function buren(stapel, id) {
  const i = (stapel || []).findIndex((s) => s.id === id);
  if (i < 0) return { positie: 0, totaal: (stapel || []).length, vorige: null, volgende: null };
  return { positie: i + 1, totaal: stapel.length, vorige: i > 0 ? stapel[i - 1].id : null,
    volgende: i < stapel.length - 1 ? stapel[i + 1].id : null };
}

/** Na indienen door naar de eerstvolgende die jij nog moet beoordelen (anders gewoon de volgende). */
export function volgendeTeBeoordelen(stapel, id) {
  const lijst = stapel || [];
  const i = lijst.findIndex((s) => s.id === id);
  const na = [...lijst.slice(i + 1), ...lijst.slice(0, Math.max(i, 0))];
  const hit = na.find((s) => s.te_beoordelen && s.id !== id);
  return hit ? hit.id : buren(lijst, id).volgende;
}

export function scorecardCompleet(criteria, scores, oordeel) {
  return (criteria || []).length > 0 && (criteria || []).every((c) => [1, 2, 3, 4].includes(scores?.[c.id]))
    && ['goedkeuren', 'afkeuren'].includes(oordeel);
}

/** Gemiddelde per criterium over de beoordelingen die je mag zien (inclusief je eigen). */
export function gemiddelden(beoordelingen, criteria) {
  const uit = {};
  for (const c of criteria || []) {
    const w = (beoordelingen || []).map((b) => b.scores?.[c.id]).filter((x) => typeof x === 'number');
    uit[c.id] = w.length ? Math.round((w.reduce((a, b) => a + b, 0) / w.length) * 10) / 10 : null;
  }
  return uit;
}

export function telOordelen(beoordelingen) {
  const uit = { goedkeuren: 0, afkeuren: 0 };
  for (const b of beoordelingen || []) if (uit[b.oordeel] !== undefined) uit[b.oordeel] += 1;
  return uit;
}

/** Vacatures filteren op status-tab en zoekterm (open-sollicitatie hoort bij 'open'). */
export function filterVacatures(vacatures, status, q) {
  const t = String(q || '').trim().toLowerCase();
  return (vacatures || []).filter((v) => v.status === status && (!t || v.titel.toLowerCase().includes(t)));
}

/** Portfolio-link → insluitbare preview (alleen YouTube, Vimeo, Behance), anders null. */
export function portfolioEmbed(url) {
  let u;
  try { u = new URL(url); } catch { return null; }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const host = u.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
  const pad = u.pathname;
  const yt = (id) => (/^[A-Za-z0-9_-]{6,20}$/.test(id || '') ? {
    soort: 'youtube', label: 'YouTube', id,
    thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    embed: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`,
  } : null);
  if (host === 'youtube.com') {
    if (pad === '/watch') return yt(u.searchParams.get('v'));
    const m = pad.match(/^\/(?:shorts|embed|live)\/([^/]+)/);
    return m ? yt(m[1]) : null;
  }
  if (host === 'youtu.be') return yt(pad.slice(1).split('/')[0]);
  if (host === 'vimeo.com') {
    const m = pad.match(/^\/(?:video\/)?(\d{5,12})(?:\/|$)/);
    return m ? { soort: 'vimeo', label: 'Vimeo', id: m[1], embed: `https://player.vimeo.com/video/${m[1]}?dnt=1&autoplay=1` } : null;
  }
  if (host === 'behance.net') {
    const m = pad.match(/^\/gallery\/(\d{4,12})(?:\/|$)/);
    return m ? { soort: 'behance', label: 'Behance', id: m[1], embed: `https://www.behance.net/embed/project/${m[1]}?ilo0=1` } : null;
  }
  return null;
}

/** Datum/tijd van een gesprek, kort ("di 8 okt 11:00"). */
export function gesprekLabel(iso) {
  const d = alsDatum(iso);
  return d ? d.toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

/** UTC-iso van de server → waarde voor <input type="datetime-local"> (lokale tijd), en terug. */
export function naarLokaalInvoer(iso) {
  const d = alsDatum(iso);
  if (!d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
export function vanLokaalInvoer(waarde) {
  if (!waarde) return null;
  const d = new Date(waarde);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Alleen wat ik nog moet beoordelen. */
/** Bewaartermijn op de kaart (AVG): tot wanneer, en waarom. Geen datum = procedure loopt nog. */
export function bewaarLabel(k) {
  if (!k || k.geanonimiseerd) return '';
  const metToestemming = k.toestemming === 'ja';
  const d = alsDatum(k.bewaren_tot);
  if (!d) return metToestemming ? 'Bewaard tot 1 jaar na de procedure (toestemming)' : 'Bewaard tot 4 weken na de procedure';
  const datum = d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  const reden = k.einde_reden === 'vangnet' ? ' · 6 maanden geen activiteit' : '';
  return `Bewaard tot ${datum}${metToestemming ? ' (toestemming 1 jaar)' : ''}${reden}`;
}

export function alleenTeBeoordelen(sollicitaties, aan) {
  return aan ? (sollicitaties || []).filter((s) => s.te_beoordelen) : (sollicitaties || []);
}

/** Sneltoets → actie, of null. Niets als de focus in een invoerveld staat. */
export function sneltoets(e) {
  const tag = (e.target?.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return null;
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (e.key === 'j' || e.key === 'ArrowDown') return { soort: 'volgende' };
  if (e.key === 'k' || e.key === 'ArrowUp') return { soort: 'vorige' };
  if (['1', '2', '3', '4'].includes(e.key)) return { soort: 'score', waarde: Number(e.key) };
  if (e.key === 'g') return { soort: 'oordeel', waarde: 'goedkeuren' };
  if (e.key === 'x') return { soort: 'oordeel', waarde: 'afkeuren' };
  if (e.key === 'Enter') return { soort: 'indienen' };
  return null;
}

// ── Teams & teamleads ──────────────────────────────────────────────────────────────

/** Teams die je in de vacaturedialoog kunt kiezen: actief, plus het huidige team van de vacature (ook gearchiveerd). */
export function kiesbareDisciplines(disciplines, huidige) {
  return (disciplines || []).filter((d) => !d.gearchiveerd || d.sleutel === huidige);
}

/** Wie er bij een wijziging van teamleads nieuw bij kwam (kleine letters). */
export function nieuweTeamleads(oud, nieuw) {
  const was = new Set((oud || []).map((e) => String(e).toLowerCase()));
  return (nieuw || []).map((e) => String(e).toLowerCase()).filter((e) => !was.has(e));
}

/** Teamleads van een team zonder werving-leesrecht (leesrecht === false; null = onbekend, dan niets tonen). */
export function zonderLeesrecht(team) {
  return (team?.teamleads_info || []).filter((t) => t.leesrecht === false);
}

/** Volgorde na slepen: alle sleutels — actieve in de nieuwe volgorde, gearchiveerde achteraan zoals ze stonden. */
export function volgordeNaSlepen(teams, vanSleutel, naarSleutel) {
  const actief = (teams || []).filter((t) => !t.gearchiveerd).map((t) => t.sleutel);
  const archief = (teams || []).filter((t) => t.gearchiveerd).map((t) => t.sleutel);
  const van = actief.indexOf(vanSleutel);
  const naar = actief.indexOf(naarSleutel);
  if (van < 0 || naar < 0 || van === naar) return null;
  const uit = [...actief];
  const [x] = uit.splice(van, 1);
  uit.splice(naar, 0, x);
  return [...uit, ...archief];
}

// ── Kleur (spaarzaam en betekenisvol) ──────────────────────────────────────────────
// Fase: zachte achtergrond + donkere tekst (contrast ≥ 4.5:1) + een verzadigde stip. Afgewezen bewust gedempt.
export const FASE_KLEUR = {
  nieuw: { tekst: '#1e4fae', bg: '#eaf1fd', stip: '#3b76e0' },
  beoordeeld: { tekst: '#55389e', bg: '#f1edfa', stip: '#7c5cd6' },
  gesprek: { tekst: '#7a4a00', bg: '#fcf2df', stip: '#d08a12' },
  aanbod: { tekst: '#0b5e63', bg: '#e4f3f3', stip: '#1a959b' },
  aangenomen: { tekst: '#1a5e34', bg: '#e6f4ea', stip: '#2f9358' },
  afgewezen: { tekst: '#6f4b4b', bg: '#f3eeee', stip: '#b48d8d' },
};
const FASE_GRIJS = { tekst: STIL, bg: '#f1f3f5', stip: '#aab3bf' };
export function faseKleur(fase) {
  return FASE_KLEUR[fase] || FASE_GRIJS;
}

// Avatar: vaste zachte kleur per naam (hash), donkere letters.
const AVATAR_KLEUREN = [
  { bg: '#e3ecfb', tekst: '#2b4d8c' }, { bg: '#ece6f8', tekst: '#4f3a8a' }, { bg: '#fbeedd', tekst: '#7a4b12' },
  { bg: '#e0f2f1', tekst: '#165e5a' }, { bg: '#e7f3e5', tekst: '#2f5f27' }, { bg: '#f8e6ea', tekst: '#7d2f45' },
  { bg: '#e9edf2', tekst: '#3d4b5f' }, { bg: '#f6efd9', tekst: '#6a5410' },
];
export function avatarKleur(naam) {
  let h = 0;
  for (const c of String(naam || '')) h = (h * 31 + c.codePointAt(0)) >>> 0;
  return AVATAR_KLEUREN[h % AVATAR_KLEUREN.length];
}
export function initialen(naam) {
  const delen = String(naam || '').trim().split(/\s+/).filter((d) => /^\p{L}/u.test(d));
  if (!delen.length) return '?';
  const eerste = delen[0][0];
  const laatste = delen.length > 1 ? delen[delen.length - 1][0] : '';
  return (eerste + laatste).toUpperCase();
}

// Score 1–4: van zwak naar sterk (gevuld vlak, witte cijfers); oordeel groen/rood zodra het gekozen is.
export const SCORE_KLEUR = { 1: '#b8622a', 2: '#a8861c', 3: '#4f8a3c', 4: '#2a7d4b' };
export const OORDEEL_KLEUR = { goedkeuren: '#2a7d4b', afkeuren: '#b5432c' };

// ── Brieftekst: lange links inkorten, Mail Drop/WeTransfer herkennen ─────────────────────────
const URL_RE = /<?(https?:\/\/[^\s<>"')\]]+)>?/g;
const BESCHIKBAAR_RE = /(?:Beschikbaar tot|Available until|Verloopt op|Expires on|Vervalt op)\s*:?\s*(\d{1,2}\s+[A-Za-zéû]+\.?\s+\d{4})/i;

/** Bestandsdeling (iCloud Mail Drop, WeTransfer) → label, anders null. Zelfde regel als de engine. */
export function bestandsdeling(url) {
  let u;
  try { u = new URL(url); } catch { return null; }
  const host = u.hostname.toLowerCase();
  const op = (d) => host === d || host.endsWith(`.${d}`);
  if (op('icloud.com') && u.pathname.toLowerCase().startsWith('/attachment')) return 'iCloud Mail Drop';
  if (op('wetransfer.com') || op('we.tl')) return 'WeTransfer';
  return null;
}

/** Korte weergave van een link: korte links ongewijzigd, lange als "domein…". */
export function korteLink(url, max = 50) {
  if (String(url).length <= max) return String(url);
  try { return `${new URL(url).hostname.replace(/^www\./, '')}…`; } catch { return `${String(url).slice(0, max)}…`; }
}

/**
 * Tekst → delen voor veilige weergave (React-tekst + <a>, nooit HTML):
 * {soort:'tekst', tekst} | {soort:'link', href, label} | {soort:'bijlage', href, label}.
 * Alleen http(s)-links worden klikbaar. Mail Drop/WeTransfer: "Bijlage via iCloud Mail Drop (beschikbaar tot …)".
 */
export function briefDelen(tekst) {
  const t = String(tekst || '');
  const tot = (t.match(BESCHIKBAAR_RE) || [])[1];
  const delen = [];
  let rest = 0;
  for (const m of t.matchAll(URL_RE)) {
    if (m.index > rest) delen.push({ soort: 'tekst', tekst: t.slice(rest, m.index) });
    const href = m[1].replace(/[.,;:!?]+$/, '');
    const staart = m[1].slice(href.length);
    const deling = bestandsdeling(href);
    if (deling) {
      delen.push({ soort: 'bijlage', href, label: `Bijlage via ${deling}${tot ? ` (beschikbaar tot ${tot})` : ''}` });
    } else {
      delen.push({ soort: 'link', href, label: korteLink(href) });
    }
    if (staart) delen.push({ soort: 'tekst', tekst: staart });
    rest = m.index + m[0].length;
  }
  if (rest < t.length) delen.push({ soort: 'tekst', tekst: t.slice(rest) });
  // Dezelfde bestandsdeling twee keer (Mail Drop zet de link vaak dubbel): één knop.
  return delen.filter((d, i) => !(d.soort === 'bijlage' && delen.slice(0, i).some((x) => x.soort === 'bijlage' && x.href === d.href)));
}

/** Eerste `max` tekens van de delen (voor "Hele brief"); een link telt met zijn label. */
export function knipDelen(delen, max) {
  const uit = [];
  let n = 0;
  for (const d of delen || []) {
    const lengte = d.soort === 'tekst' ? d.tekst.length : d.label.length;
    if (n + lengte <= max) { uit.push(d); n += lengte; continue; }
    if (d.soort === 'tekst') uit.push({ soort: 'tekst', tekst: `${d.tekst.slice(0, Math.max(0, max - n))}…` });
    break;
  }
  return uit;
}

export function delenLengte(delen) {
  return (delen || []).reduce((n, d) => n + (d.soort === 'tekst' ? d.tekst.length : d.label.length), 0);
}

/** Portfolio-link → soort + label (voor het icoon en de tekst). */
export function portfolioSoort(url) {
  let u;
  try { u = new URL(url); } catch { return { soort: 'website', label: String(url) }; }
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  const op = (d) => host === d || host.endsWith(`.${d}`);
  if (op('linkedin.com')) return { soort: 'linkedin', label: 'LinkedIn-profiel' };
  if (op('youtube.com') || op('youtu.be')) return { soort: 'youtube', label: 'YouTube' };
  if (op('vimeo.com')) return { soort: 'vimeo', label: 'Vimeo' };
  if (op('behance.net')) return { soort: 'behance', label: 'Behance' };
  if (op('filmvacatures.nl') && /^\/profiles\/view\//.test(u.pathname)) return { soort: 'profiel', label: 'Profiel op filmvacatures.nl' };
  if (op('filmpeople.nl') || op('filmpeople.com')) return { soort: 'profiel', label: 'Profiel op Filmpeople' };
  return { soort: 'website', label: host + (u.pathname.length > 1 ? korteLink(u.pathname, 30) : '') };
}

// ── Koppelen, stagetypes, concepten (feedback Joanne, 8 okt 2026) ─────────────────────────

/** Vacatures om (opnieuw) aan te koppelen: alles behalve gesloten, plus de huidige (ook als die intussen dicht is). */
export function koppelOpties(vacatures, huidigId) {
  return (vacatures || []).filter((v) => v.status !== 'gesloten' || v.id === huidigId);
}

/** Pipeline-filter op stagetype: null = alles, '_geen' = stages zonder type, '_overig' = geen stage, anders de sleutel. */
export function filterStagetype(sollicitaties, sleutel) {
  const lijst = sollicitaties || [];
  if (!sleutel) return lijst;
  if (sleutel === '_geen') return lijst.filter((s) => s.categorie === 'stage' && !s.stagetype);
  if (sleutel === '_overig') return lijst.filter((s) => s.categorie !== 'stage' && !s.stagetype);
  return lijst.filter((s) => s.stagetype === sleutel);
}

/** Regels voor de onderverdeling van de open ingang in het overzicht (lopende sollicitaties per stagetype). */
export function stageRegels(indeling) {
  if (!indeling) return [];
  const uit = (indeling.stagetypes || []).map((t) => ({ sleutel: t.sleutel, label: t.label, n: t.in_proces || 0 }));
  if (indeling.niet_ingedeeld) uit.push({ sleutel: '_geen', label: 'Stage, nog niet ingedeeld', n: indeling.niet_ingedeeld });
  if (indeling.overig) uit.push({ sleutel: '_overig', label: 'Open sollicitatie', n: indeling.overig });
  return uit;
}

/** Melding als er voor de fase geen actief sjabloon is: welke soort ontbreekt en welke sjablonen er uit staan. */
export function conceptAdviesTekst(advies) {
  if (!advies) return '';
  const stappen = advies.stappen || [];
  const soort = stappen.length ? stappen.join(' of ').toLowerCase() : 'een antwoord';
  const fase = advies.fase_label ? ` in de fase ${advies.fase_label}` : '';
  const uit = advies.uitgeschakeld || [];
  const staatUit = uit.length
    ? ` ${uit.length === 1 ? 'Dit sjabloon staat uit' : 'Deze sjablonen staan uit'}: ${uit.map((n) => `“${n}”`).join(', ')}.`
    : ' Daar is nog geen sjabloon voor.';
  return `Geen actief sjabloon voor ${soort}${fase}.${staatUit}`;
}

/** Van het beheerpad uit de API een pad binnen de app (alleen /admin/…; nooit een externe link). */
export function beheerPad(url) {
  const u = String(url || '');
  return u.startsWith('/admin/') ? u : '/admin/settings?tab=mailboxen&section=ikwilwerken';
}
